import { useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import {
  updateEmail,
  updatePhone,
} from '../../services';
import { getInitialSecurityForm, validatePasswordChange } from '../../utils';
import { toast } from '../../../../shared/hooks/use-toast';

export function useSecuritySettings() {
  const { currentUser, isAuthenticated, changePassword } = useSession();
  const [security, setSecurity] = useState(getInitialSecurityForm());
  const [passwordStatus, setPasswordStatus] = useState({
    isSubmitting: false,
    error: '',
    success: '',
  });
  const [editMode, setEditMode] = useState({
    email: false,
    password: false,
    phone: false,
  });

  function updateField(field, value) {
    setSecurity((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function togglePasswordVisibility(field) {
    setSecurity((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  }

  function openSection(section) {
    if (section === 'password') {
      setPasswordStatus({
        isSubmitting: false,
        error: '',
        success: '',
      });
    }

    setEditMode((prev) => ({
      ...prev,
      [section]: true,
    }));
  }

  function closeSection(section) {
    if (section === 'password') {
      setSecurity((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setPasswordStatus({
        isSubmitting: false,
        error: '',
        success: '',
      });
    }

    setEditMode((prev) => ({
      ...prev,
      [section]: false,
    }));
  }

  async function submitEmailChange() {
    if (!currentUser) return;

    const result = await updateEmail(security.newEmail);
    if (!result.ok) {
      toast({ title: 'Email non aggiornata', description: result.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Email aggiornata', description: result.message });
    setSecurity((prev) => ({ ...prev, newEmail: '' }));
    closeSection('email');
  }

  async function submitPasswordChange() {
    if (!currentUser) return;
    setPasswordStatus({
      isSubmitting: false,
      error: '',
      success: '',
    });

    const error = validatePasswordChange(security);
    if (error) {
      setPasswordStatus({
        isSubmitting: false,
        error,
        success: '',
      });
      return;
    }

    setPasswordStatus({
      isSubmitting: true,
      error: '',
      success: '',
    });

    try {
      await changePassword({
        currentPassword: security.currentPassword,
        newPassword: security.newPassword,
      });
      setSecurity(getInitialSecurityForm());
      setEditMode((prev) => ({
        ...prev,
        password: false,
      }));
      setPasswordStatus({
        isSubmitting: false,
        error: '',
        success: 'Password cambiata con successo.',
      });
    } catch (changeError) {
      setPasswordStatus({
        isSubmitting: false,
        error: changeError.message || 'Cambio password non riuscito.',
        success: '',
      });
    }
  }

  function submitPhoneChange() {
    if (!currentUser) return;

    const result = updatePhone(security.newPhone);
    if (!result.ok) {
      toast({ title: 'Telefono non aggiornato', description: result.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Telefono aggiornato', description: result.message });
    setSecurity((prev) => ({ ...prev, newPhone: '' }));
    closeSection('phone');
  }

  return {
    currentUser,
    isAuthenticated,
    security,
    passwordStatus,
    editMode,
    updateField,
    togglePasswordVisibility,
    openSection,
    closeSection,
    submitEmailChange,
    submitPasswordChange,
    submitPhoneChange,
  };
}
